import Backbone from 'backbone'
import _ from 'lodash'
import template from './visible-linked-field-form.hbs'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import renderFieldDropdown from 'scoreboard/datasets/render-field-dropdown'
import selects from 'scoreboard/components/selects'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import datasetPaths from './dataset-paths'
import i18n from 'scoreboard/components/i18n'
import highlight from 'scoreboard/components/highlight'
import fieldAggregationDescription from 'scoreboard/datasets/field-aggregation-description'
import Dataset from 'scoreboard/datasets/model/dataset'
const { isTotal, TOTAL } = Dataset
import unwrapModel from 'scoreboard/components/unwrap-model'
import PathToDatasetButton from 'scoreboard/datasets/edit/path-to-dataset-button'
import arrayToHash from 'scoreboard/components/array-to-hash'
import constants from 'scoreboard/components/constants'
const { UNSAVED_PREFIX } = constants

const VisibleLinkedFieldForm = Backbone.View.extend({

	className: 'visible-linked-field-form',

	/**
	 * A form used to edit which linked fields should be visible and how they
	 * should be linked (#23489 #20543)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model
	 *
	 * @param {Object} [options.visibleLinkedField] an existing
	 * visibleLinkedField to be edited
	 *
	 * @param {Boolean} [options.add=false] true if form is adding a new VLF
	 * @param {Array} [options.shortestPathsByDatasetId] a map of the shortest path
	 * to each linked field for the given dataset to the root dataset id
	 */
	initialize: function(options = {}) {
		this.options = options
	},

	tagName: 'form',

	events: {
		'change [name=linkedDatasetId]': 'onChangeDataset',
		'change [name=fieldId]': 'onChangeField'
	},

	/** @type {Dataset} */
	model: null,

	render: function() {
		this.$el.empty().append(template())

		this.renderDatasetDropdown()
		this.renderPathToDatasetButton(_.get(this.options.visibleLinkedField, 'path'))
		this.renderLabelField()
		this.renderFieldSelect()

		formHelper.writeToDom(this.options.visibleLinkedField, this.el)

		this.setupFormView()

		this.toggleControls()

		return this
	},

	renderDatasetDropdown: function() {
		let select = this.$('[name=linkedDatasetId]')
		let path = _.get(this.options.visibleLinkedField, 'path')
		let selectedId = datasetPaths.lastDatasetId(path)
		if (_.size(this.model.get('linkedDatasets')) == 1) {
			selectedId = this.model.get('linkedDatasets')[0].id
		}
		selects.filtering(select, {
			options: this.model.get('linkedDatasets'),
			accessors: {
				value: 'id'
			}
		})
		selects.val(select, selectedId)
		this.datasetMarkedField = new MarkedField({
			el: select,
			validators: [requiredValidator]
		}).render()
	},

	renderPathToDatasetButton: function(path) {
		this.pathToDatasetButton && this.pathToDatasetButton.remove()
		this.pathToDatasetButton = new PathToDatasetButton({
			model: this.model,
			path,
			datasetIdRetriever: () => this.$('[name=linkedDatasetId]').val()
		})
		this.$('.path-to-dataset-button-container')
			.append(this.pathToDatasetButton.el)
		this.pathToDatasetButton.render()
		this.pathToDatasetButton.on('change:dataset', () => {
			this.formView.clearErrors()
			this.formView.change()
		})
	},

	renderLabelField: function() {
		this.labelField = new MarkedField({
			el: this.$('[name=label]'),
			validator: requiredValidator
		}).render()
	},

	renderFieldSelect: function(datasetId) {
		datasetId = datasetId || this.getDatasetId()
		if (!datasetId) return

		let dataset = this.model.datasetsById[datasetId]
		let onlyUniquePath = _.includes(this.model.get('onlyUniquePaths'), parseInt(datasetId))
		let includeNativeTotal = true
		let allowedFieldIds = _.map(dataset.get('fields'), 'id')
		if (onlyUniquePath && this.options.add) {
			let vlfs = _.filter(this.model.get('visibleLinkedFields'), vlf => {
				return 	vlf.linkedDatasetId == datasetId || datasetId == datasetPaths.lastDatasetId(vlf.path)
			})
			let vlfFieldIds = arrayToHash(_.map(vlfs, 'fieldId'))
			if (vlfFieldIds[TOTAL]) {
				includeNativeTotal = false
			}
			allowedFieldIds = _.filter(allowedFieldIds, fieldId => {
				return _.isNil(vlfFieldIds[fieldId])
			})
		}

		// show a banner message that all fields for the dataset were already
		// added (if appropriate)
		let allFieldsAdded = _.isEmpty(allowedFieldIds) && !includeNativeTotal
		this.$('.banner').toggleClass('is-not-displayed',
			!allFieldsAdded)

		let selectedId = _.get(this.options.visibleLinkedField, 'fieldId')
		this.fieldField = renderFieldDropdown({
			el: this.$('[name=fieldId]'),
			dataset,
			allowedFieldIds,
			includeNativeTotal,
			filterVisibleLinkedFields: false,
			required: true,
			selectedId
		})
	},

	setupFormView: function() {
		let MyFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyFormView({
			el: this.el,
			submit: this.submit.bind(this)
		})
		this.formView.start()
	},

	/**
	 * Returns the ID of the dataset that should be selected
	 */
	getDatasetId: function() {
		let fieldId = _.get(this.options.visibleLinkedField, 'fieldId')
		// check for a datasetId here because even if we are creating a
		// new visibleLinkedField, the dropdown could already have a
		// dataset selected if we previously visited the form
		let datasetId = this.$('[name=linkedDatasetId]').val()
		if (fieldId && !isTotal(fieldId)) {
			let field = this.model.combinedFieldsById[fieldId]
			datasetId = field.datasetId
		}
		return datasetId
	},

	/**
	 * Callback executed after the dataset is changed via a dropdown control (vs
	 * the second-level dialog that allows specifying a path to the dataset)
	 */
	onChangeDataset: function() {
		let datasetId = this.$('[name=linkedDatasetId]').val()
		this.toggleControls()
		this.$('.banner').addClass('is-not-displayed')
		if (datasetId) {
			// no field will be selected anymore, so clear out the label
			if (this.isAutomaticallyPopulatingLabel()) {
				this.$('[name=label]').val('')
			}
			this.renderFieldSelect(datasetId)
			const shortestPath = this.options.shortestPathsByDatasetId[datasetId]
			this.renderPathToDatasetButton(_.get(shortestPath, 'id'))
		}
	},

	/**
	 * Toggles the visibility of controls based on the selection of the dataset
	 */
	toggleControls: function() {
		let datasetId = parseInt(this.$('[name=linkedDatasetId]').val())
		if (_.isNil(datasetId) || _.isNaN(datasetId)) {
			this.$('.field-id, .field-name, .path-to-dataset-button-container')
				.addClass('is-not-displayed')
		} else {
			this.$('.field-id, .field-name').removeClass('is-not-displayed')
			this.$('.path-to-dataset-button-container')
				.toggleClass('is-not-displayed', !this.needsPath())
		}
	},

	needsPath: function() {
		let datasetId = parseInt(this.$('[name=linkedDatasetId]').val())
		return !_.includes(this.model.get('onlyUniquePaths'), datasetId)
	},

	onChangeField: function() {
		// when a field is chosen, automatically update the label unless the
		// user previously typed something in
		if (this.isAutomaticallyPopulatingLabel()) {
			let fieldId = this.$('[name=fieldId]').val()
			if (fieldId) {
				let label
				if (isTotal(fieldId)) {
					let datasetId = parseInt(this.$('[name=linkedDatasetId]').val())
					let dataset = this.model.datasetsById[datasetId]
					label = fieldAggregationDescription({
						dataset: this.model,
						tracked: unwrapModel(dataset).tracked
					})
				} else {
					label = this.model.combinedFieldsById[fieldId].label
				}
				this.$('[name=label]').val(label)
			}
		}
	},

	/**
	 * Returns whether we should be automatically populating the "Field Name
	 * in this Dataset" field (the label for the VisibleLinkedField). We
	 * automatically populate this field if the user never typed anything into
	 * it
	 */
	isAutomaticallyPopulatingLabel: function() {
		let label = this.$('[name=label]').val()
		let fieldEl = this.$('[name=fieldId]')
		let previousFieldId = selects.previousValue(fieldEl)
		let previousField = this.model.combinedFieldsById[previousFieldId]
		return _.isEmpty(label) ||
			label == _.get(previousField, 'label') ||
			_.isNil(previousField)
	},

	readFromDom: function() {
		let data = formHelper.readFromDom(this.el)
		data.id = _.get(this.options.visibleLinkedField, 'id', _.uniqueId(UNSAVED_PREFIX))
		data.path = this.pathToDatasetButton.path
		return data
	},

	test: function() {
		let data = this.readFromDom()
		let duplicate = _.find(this.model.get('visibleLinkedFields'), vlf => {
			return vlf.id != data.id &&
				vlf.path == data.path &&
				vlf.fieldId == data.fieldId &&
				vlf.linkedDatasetId == data.linkedDatasetId
		})
		if (duplicate) {
			let labelHtml = highlight(duplicate.label)
			return i18n.get('datasets.linked.unique', labelHtml)
		}

		if (this.needsPath() && !_.isEmpty(data.linkedDatasetId) && !_.isEmpty(data.fieldId)
			&& _.isEmpty(this.pathToDatasetButton.path)) {
			return i18n.get('datasets.linked.choosePathToDataset.required')
		}

		return FormView.prototype.test.apply(this, arguments)
	},

	submit: function() {
		this.trigger('done', this.readFromDom())
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.labelField, this.fieldField, this.formView,
			this.datasetMarkedField, this.pathToDatasetButton], 'remove')
	}

})

export default VisibleLinkedFieldForm

VisibleLinkedFieldForm.__filename__ = 'datasets/edit/visible-linked-field-form.js'
